/**
 * Individual solicitud item component with cascading dropdowns
 * Categoría → Item (filtered by categoria)
 * Categoría → Falla (filtered by categoria)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Loader2 } from 'lucide-react';
import PhotoUpload from '../PhotoUpload';
import VoiceInput from '../VoiceInput';

const SolicitudItem = ({ 
  solicitud, 
  index, 
  canDelete, 
  onUpdate, 
  onUpdateFotos, 
  onRemove,
  // Catálogos
  categorias = [],
  lugares = [],
  items = [],
  fallas = []
}) => {
  // Loading states for cascading effect
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingFallas, setLoadingFallas] = useState(false);

  // Filter items based on selected categoria
  const filteredItems = useMemo(() => {
    if (!solicitud.categoria) return [];
    // Show items that match the selected categoria OR items with no categoria assigned
    return items.filter(item => 
      item.activo !== false && 
      (item.categoria_nombre === solicitud.categoria || !item.categoria_nombre)
    );
  }, [solicitud.categoria, items]);

  // Filter fallas based on selected categoria (changed from item)
  const filteredFallas = useMemo(() => {
    if (!solicitud.categoria) return [];
    // Show fallas that match the selected categoria OR fallas with no categoria assigned
    return fallas.filter(falla => 
      falla.activo !== false && 
      (falla.categoria_nombre === solicitud.categoria || !falla.categoria_nombre)
    );
  }, [solicitud.categoria, fallas]);

  // Handle categoria change - reset item and falla, show loading
  const handleCategoriaChange = (value) => {
    onUpdate(index, 'categoria', value);
    // Reset dependent fields
    onUpdate(index, 'item', '');
    onUpdate(index, 'falla', '');
    
    // Show loading spinner briefly
    setLoadingItems(true);
    setLoadingFallas(true);
    setTimeout(() => {
      setLoadingItems(false);
      setLoadingFallas(false);
    }, 300);
  };

  // Handle item change (no longer resets falla since falla depends on categoria)
  const handleItemChange = (value) => {
    onUpdate(index, 'item', value);
  };

  // Handle falla change
  const handleFallaChange = (value) => {
    onUpdate(index, 'falla', value);
  };

  // Filter active categorias and lugares
  const activeCategorias = useMemo(() => 
    categorias.filter(c => c.activo !== false), [categorias]);
  
  const activeLugares = useMemo(() => 
    lugares.filter(l => l.activo !== false), [lugares]);

  return (
    <div className="border border-[#E8E8E7] rounded-lg p-3 sm:p-4 bg-[#FAFAFA] relative">
      <div className="flex justify-between items-center mb-2 sm:mb-3">
        <span className="text-xs sm:text-sm font-semibold text-[#9d8272]">Solicitud #{index + 1}</span>
        {canDelete && (
          <Button
            type="button"
            onClick={() => onRemove(index)}
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
            data-testid={`remove-solicitud-${index}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {/* Categoría - Level 1 */}
        <div>
          <Label className="text-[10px] sm:text-xs">Categoría *</Label>
          <Select
            value={solicitud.categoria}
            onValueChange={handleCategoriaChange}
          >
            <SelectTrigger 
              className="border-[#c5b8a2] text-xs sm:text-sm h-8 sm:h-10" 
              data-testid={`categoria-${index}`}
            >
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              {activeCategorias.map((cat) => (
                <SelectItem key={cat.id} value={cat.nombre}>
                  {cat.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Lugar */}
        <div>
          <Label className="text-[10px] sm:text-xs">Lugar *</Label>
          <Select
            value={solicitud.lugar}
            onValueChange={(val) => onUpdate(index, 'lugar', val)}
          >
            <SelectTrigger 
              className="border-[#c5b8a2] text-xs sm:text-sm h-8 sm:h-10" 
              data-testid={`lugar-${index}`}
            >
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent>
              {activeLugares.map((lugar) => (
                <SelectItem key={lugar.id} value={lugar.nombre}>
                  {lugar.nombre}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Item - Level 2 (depends on Categoría) */}
        <div>
          <Label className="text-[10px] sm:text-xs">Item *</Label>
          <div className="relative">
            <Select
              value={solicitud.item}
              onValueChange={handleItemChange}
              disabled={!solicitud.categoria || loadingItems}
            >
              <SelectTrigger 
                className={`border-[#c5b8a2] text-xs sm:text-sm h-8 sm:h-10 ${
                  !solicitud.categoria ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid={`item-${index}`}
              >
                {loadingItems ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Cargando...</span>
                  </div>
                ) : (
                  <SelectValue placeholder={!solicitud.categoria ? "Seleccione categoría" : "Seleccionar"} />
                )}
              </SelectTrigger>
              <SelectContent>
                {filteredItems.length === 0 ? (
                  <div className="py-2 px-3 text-xs text-gray-500">
                    No hay items disponibles
                  </div>
                ) : (
                  filteredItems.map((item) => (
                    <SelectItem key={item.id} value={item.nombre}>
                      {item.nombre}
                      {item.categoria_nombre && (
                        <span className="text-[10px] text-gray-400 ml-1">
                          ({item.categoria_nombre})
                        </span>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {loadingItems && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded">
                <Loader2 className="h-4 w-4 animate-spin text-[#9d8272]" />
              </div>
            )}
          </div>
          {!solicitud.categoria && (
            <p className="text-[9px] text-gray-400 mt-0.5">Primero seleccione categoría</p>
          )}
        </div>

        {/* Falla - Level 2 (depends on Categoría) */}
        <div>
          <Label className="text-[10px] sm:text-xs">Falla *</Label>
          <div className="relative">
            <Select
              value={solicitud.falla}
              onValueChange={handleFallaChange}
              disabled={!solicitud.categoria || loadingFallas}
            >
              <SelectTrigger 
                className={`border-[#c5b8a2] text-xs sm:text-sm h-8 sm:h-10 ${
                  !solicitud.categoria ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid={`falla-${index}`}
              >
                {loadingFallas ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Cargando...</span>
                  </div>
                ) : (
                  <SelectValue placeholder={!solicitud.categoria ? "Seleccione categoría" : "Seleccionar"} />
                )}
              </SelectTrigger>
              <SelectContent>
                {filteredFallas.length === 0 ? (
                  <div className="py-2 px-3 text-xs text-gray-500">
                    No hay fallas disponibles
                  </div>
                ) : (
                  filteredFallas.map((falla) => (
                    <SelectItem key={falla.id} value={falla.nombre}>
                      {falla.nombre}
                      {falla.categoria_nombre && (
                        <span className="text-[10px] text-gray-400 ml-1">
                          ({falla.categoria_nombre})
                        </span>
                      )}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {loadingFallas && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded">
                <Loader2 className="h-4 w-4 animate-spin text-[#9d8272]" />
              </div>
            )}
          </div>
          {!solicitud.categoria && (
            <p className="text-[9px] text-gray-400 mt-0.5">Primero seleccione categoría</p>
          )}
        </div>

        {/* Descripción */}
        <div className="col-span-2 sm:col-span-4">
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[10px] sm:text-xs">Descripción *</Label>
            <VoiceInput
              onTranscript={(text, isFinal) => {
                if (isFinal) {
                  const currentDesc = solicitud.descripcion || '';
                  const newDesc = currentDesc ? `${currentDesc} ${text}` : text;
                  onUpdate(index, 'descripcion', newDesc);
                }
              }}
            />
          </div>
          <Textarea
            value={solicitud.descripcion}
            onChange={(e) => onUpdate(index, 'descripcion', e.target.value)}
            className="border-[#c5b8a2] text-xs sm:text-sm"
            rows={2}
            placeholder="Describa el problema o use el micrófono para dictar..."
            data-testid={`descripcion-${index}`}
          />
        </div>

        {/* Archivos */}
        <div className="col-span-2 sm:col-span-4">
          <Label className="text-[10px] sm:text-xs mb-2 block">Archivos (opcional, máx. 3)</Label>
          <PhotoUpload
            photos={solicitud.fotos || []}
            onChange={(fotos) => onUpdateFotos(index, fotos)}
            maxPhotos={3}
          />
        </div>
      </div>
    </div>
  );
};

export default SolicitudItem;
